// ---------------------------------------------------------------------------
// A held cart, on paper.
//
// THIS IS NOT A RECEIPT, and the whole design of it is about making that
// impossible to miss. A customer handed this while their meat is still being
// cut must not later believe they have paid, and a cashier finding it on the
// counter must not file it as a sale.
//
// So it says so three times, in three registers:
//
//   * a heavy inverted banner at the top - HELD ORDER, NOT PAID - which is the
//     first thing the eye lands on and survives being read upside down across
//     a counter;
//   * a plain sentence under the totals saying no money has been taken and no
//     stock has moved;
//   * the absence of everything a receipt has - no receipt number, no payment
//     method, no amount tendered, no change, no thank-you line.
//
// The total is called "Amount due", never "Total paid".
//
// It borrows the receipt's WIDTH and the shop's header from the active receipt
// design, so it comes out of the same roll looking like it belongs to the same
// shop - but it is deliberately not the ReceiptSheet component. Reusing that
// would mean one `isHeld` flag threaded through a component whose entire job
// is to state what somebody paid, and one missed branch would print a held
// cart that looks exactly like a paid receipt.
// ---------------------------------------------------------------------------

import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { ar as arabic, en as english } from '../../i18n/translations';
import { contentWidthMm, type ReceiptWidth } from '../../utils/printPaper';
import { optionLine } from '../../utils/optionLabel';
import { formatQuantity, formatUsd, toDecimal, unitLabel } from '../../utils/format';
import type { ReceiptConfig } from '../../types/design';
import type { HeldOrder } from '../../types';

interface Props {
  config: ReceiptConfig;
  held: HeldOrder;
  shop: { nameEn: string; nameAr: string; address: string; phone: string };
  /// A transport charge typed on the till but not yet part of any sale.
  deliveryFeeUsd?: string;
  /// The designer-style preview flag, so two of these on one page cannot both
  /// claim the id the print stylesheet targets.
  forPreview?: boolean;
}

export function HeldOrderSheet({ config, held, shop, deliveryFeeUsd, forPreview = false }: Props) {
  const { locale } = useI18n();

  const paperWidth = (config.widthMm === 58 ? 58 : 80) as ReceiptWidth;
  const previewMm = contentWidthMm(paperWidth);

  // The PAPER's language, from the receipt design - not the screen's. A shop
  // that prints Arabic receipts prints Arabic held orders.
  const printLocale = config.language === 'AR' ? 'ar' : config.language === 'EN' ? 'en' : locale;
  const h = (printLocale === 'ar' ? arabic : english).heldSheet;

  const itemsTotal = held.items.reduce(
    (sum, item) => sum.plus(toDecimal(item.lineTotalUsd)),
    toDecimal('0'),
  );
  const fee = toDecimal(deliveryFeeUsd ?? '0');
  const due = itemsTotal.plus(fee).toDecimalPlaces(2);

  const held_at = new Date(held.createdAt);
  const stamp = held_at.toLocaleString(printLocale === 'ar' ? 'ar-LB' : 'en-GB', {
    timeZone: 'Asia/Beirut',
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <div
      id={forPreview ? undefined : 'held-order-sheet'}
      className="held-order-sheet receipt-sheet bg-white p-3 font-mono text-[11px] leading-snug text-black"
      style={{ width: `${previewMm}mm` }}
      dir="ltr"
    >
      {/* ================= the shop ================= */}
      <div className="text-center">
        {config.showBusinessNameEn ? <p className="font-bold">{shop.nameEn}</p> : null}
        {config.showBusinessNameAr ? <p dir="rtl">{shop.nameAr}</p> : null}
        {config.showAddress && shop.address ? <p>{shop.address}</p> : null}
        {config.showPhone && shop.phone ? <p dir="ltr">{shop.phone}</p> : null}
      </div>

      {/* ================= what this is ================= */}
      {/* Inverted, because on a thermal roll a black block is the only thing
          that reads from across a counter and the only thing that survives a
          photocopy. */}
      <div className="my-2 bg-black px-2 py-1.5 text-center text-white">
        <p className="text-[13px] font-bold tracking-wide">{h.banner}</p>
        <p className="text-[11px] font-bold">{h.notPaid}</p>
      </div>

      {/* ================= which cart ================= */}
      <div className="border-b border-dashed border-black pb-1.5">
        <Row label={h.reference} value={held.holdNumber} />
        <Row label={h.printed} value={stamp} />
        <Row label={h.cashier} value={held.cashier.fullName} />
        {held.customer?.name ?? held.customerName ?? held.label ? (
          <Row
            label={h.customer}
            value={held.customer?.name ?? held.customerName ?? held.label ?? ''}
          />
        ) : null}
      </div>

      {/* ================= the goods ================= */}
      <div className="py-1.5">
        {held.items.map((item) => {
          const quantity = toDecimal(item.quantity);
          const weighed = item.sellingUnit === 'KG' || item.sellingUnit === 'GRAM';

          return (
            <div key={item.id} className="mb-1.5 last:mb-0">
              <p className="font-bold">
                {printLocale === 'ar' ? item.nameAr : item.nameEn}
                {optionLine(
                  { ar: item.variantLabelAr, en: item.variantLabelEn },
                  printLocale === 'ar' ? item.variantNameAr : item.variantNameEn,
                  printLocale,
                )
                  ? ` - ${optionLine(
                      { ar: item.variantLabelAr, en: item.variantLabelEn },
                      printLocale === 'ar' ? item.variantNameAr : item.variantNameEn,
                      printLocale,
                    )}`
                  : ''}
              </p>

              <div className="flex justify-between">
                <span>
                  {/* The weights rule: zeros trimmed, grams kept - the
                      same formatter as the receipt, because a customer
                      holding both sheets should read the same number. */}
                  {weighed ? formatQuantity(quantity) : quantity.toFixed(0)}
                  {' '}
                  {unitLabel(item.sellingUnit, printLocale)}
                  {' × '}
                  {formatUsd(item.unitPriceUsd)}
                </span>
                <span className="font-bold">{formatUsd(item.lineTotalUsd)}</span>
              </div>

              {item.note ? <p className="ps-2 italic">{item.note}</p> : null}
            </div>
          );
        })}
      </div>

      {/* ================= what it comes to ================= */}
      <div className="border-t border-dashed border-black pt-1.5">
        <Row label={h.subtotal} value={formatUsd(itemsTotal)} />
        {/* Only when there is one. A zero fee on a walk-in is noise. */}
        {fee.greaterThan(0) ? <Row label={h.deliveryFee} value={formatUsd(fee)} /> : null}

        <div className="mt-1 flex justify-between border-t border-black pt-1 text-[13px] font-bold">
          {/* "Amount due", never "Total paid". Nothing has been paid. */}
          <span>{h.amountDue}</span>
          <span>{formatUsd(due)}</span>
        </div>
      </div>

      {held.orderNote ? (
        <div className="mt-1.5 border-t border-dashed border-black pt-1.5">
          <p className="font-bold">{h.notes}</p>
          <p>{held.orderNote}</p>
        </div>
      ) : null}

      {/* ================= said again, in words ================= */}
      <div className={clsx('mt-2 border border-black p-1.5 text-center font-bold')}>
        <p>{h.notASale}</p>
        <p>{h.stockNotDeducted}</p>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span>{label}</span>
      <span className="text-end font-bold">{value}</span>
    </div>
  );
}
